// Shared a11y helpers for modal overlays (paywall, onboarding): lock background
// scroll and hide the rest of the page from assistive tech + pointer while a
// modal is open. WCAG: a modal dialog must make the background inert so screen
// readers and keyboard focus stay inside it. Pure DOM; jsdom-tested.

let scrollLockCount = 0;
let prevOverflow: string | null = null;

/**
 * Prevent background scroll while a modal is open. Ref-counted so nested modals
 * (e.g. onboarding → paywall) don't prematurely restore. Returns a release fn
 * that is safe to call once.
 */
export function lockScroll(doc: Document = document): () => void {
  const body = doc.body;
  if (scrollLockCount === 0 && body) {
    prevOverflow = body.style.overflow;
    body.style.overflow = 'hidden';
  }
  scrollLockCount++;
  let released = false;
  return () => {
    if (released) return;
    released = true;
    scrollLockCount = Math.max(0, scrollLockCount - 1);
    if (scrollLockCount === 0 && body) {
      body.style.overflow = prevOverflow ?? '';
      prevOverflow = null;
    }
  };
}

/**
 * Hide every body child except `overlay` from assistive tech (aria-hidden) and
 * interaction (inert). Returns a restore fn that reinstates prior attributes.
 */
export function hideSiblings(overlay: HTMLElement): () => void {
  const doc = overlay.ownerDocument;
  const body = doc.body;
  if (!body) return () => {};
  const restores: Array<() => void> = [];
  for (const node of Array.from(body.children)) {
    if (node === overlay || !(node instanceof (doc.defaultView?.HTMLElement ?? HTMLElement))) {
      continue;
    }
    const el = node as HTMLElement;
    const hadAria = el.hasAttribute('aria-hidden');
    const prevAria = el.getAttribute('aria-hidden');
    const prevInert = el.inert === true;
    el.setAttribute('aria-hidden', 'true');
    el.inert = true;
    restores.push(() => {
      if (hadAria && prevAria !== null) el.setAttribute('aria-hidden', prevAria);
      else el.removeAttribute('aria-hidden');
      el.inert = prevInert;
    });
  }
  return () => {
    for (const r of restores) r();
  };
}

/** Lock scroll + hide background siblings. Returns a single release fn. */
export function trapBackground(overlay: HTMLElement): () => void {
  const releaseScroll = lockScroll(overlay.ownerDocument);
  const restoreSiblings = hideSiblings(overlay);
  return () => {
    restoreSiblings();
    releaseScroll();
  };
}
